import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { PageTransition } from "../components/ui/PageTransition";

export function PrivacyPage(): ReactNode {
  return (
    <main className="legal-page">
      <PageTransition>
        <div className="legal-content">
          <h1>Privacy Policy</h1>
          <p className="legal-updated">Last updated: August 2026</p>

          <h2>1. Information We Collect</h2>
          <p>
            We collect the information you provide when creating an account (name, email) and the
            financial data you upload (bank statements, transaction records, receipts). When you
            sign in, we record the network address and browser identity of the connection as part of
            our security log. If you send feedback through the app, we store the text you write.
          </p>
          <p>
            If you upload a photo of a receipt, the photo may carry location metadata (GPS
            coordinates and a timestamp) recorded by your camera. We extract and store that location
            data with the receipt so it is available to support the expense if it is ever questioned
            for tax purposes. If you prefer not to share location, turn off location access for your
            camera before photographing receipts, or upload scanned documents, which usually carry
            no location metadata.
          </p>

          <h2>2. How We Use Your Information</h2>
          <p>
            Your financial data is used solely to provide the bookkeeping service: importing
            transactions, generating AI categorization suggestions, and organizing your financial
            records. We do not use your financial data for advertising or sell it to third parties.
          </p>

          <h2>3. AI Processing</h2>
          <p>
            To generate categorization suggestions, we send an AI system the transaction
            description, the transaction amount rounded up to a whole dollar, your business type,
            and the account names from your chart of accounts. To read receipts, we send up to the
            first 2,000 characters of the text extracted from the receipt. Before the description
            and the receipt text are sent, we automatically redact email addresses and number
            patterns that look like Social Security, phone, or bank account numbers. Your data is
            not used to train AI models.
          </p>

          <h2>4. Data Storage and Security</h2>
          <p>
            Your data is stored on secure, privately managed infrastructure. All connections are
            encrypted via TLS. Access to your data is restricted to authenticated sessions tied to
            your account.
          </p>

          <h2>5. Data Retention and Deletion</h2>
          <p>
            Your data is retained for as long as your account is active. You may request deletion of
            your account and all associated data at any time.
          </p>

          <h2>6. Third-Party Services</h2>
          <p>
            We use third-party AI providers to process categorization and receipt-reading requests.
            We share only what section 3 describes, and we never send your login credentials or your
            stored location data. Our automatic redaction is pattern-based: it removes email
            addresses and numbers shaped like Social Security, phone, or bank account numbers, but
            it cannot recognize personal names — so a name appearing inside a transaction
            description or printed on a receipt may reach the provider as part of that text. We do
            not sell your data.
          </p>

          <h2>7. Changes to This Policy</h2>
          <p>
            We may update this policy from time to time. We will notify you of significant changes
            via the email address associated with your account.
          </p>

          <div className="legal-nav">
            <Link to="/signup">Back to sign up</Link>
            {" | "}
            <Link to="/terms">Terms of Service</Link>
          </div>
        </div>
      </PageTransition>
    </main>
  );
}
